import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { isEqual } from 'lodash';

import { SecondaryButton } from '@magoosh/lib/button';
import { CustomSelect } from '@magoosh/lib/forms';
import StartButton from '@magoosh/organisms/practice_sessions/start_button';
import useAuthenticityToken from '@magoosh/utils/use_authenticity_token';
import { paths } from 'config/path_helpers';
import { MockTest } from 'types/mock_test';
import { minutesFormattedasHoursAndMinutesString } from 'utilities/time';

import * as api from '@test_prep/app/api';
import { useAppContext } from '@test_prep/app/context';

import TemplateOption from './template_option';
import { MockTestTemplate, SectionOption } from '../../types';

import LayersIcon from './assets/layers_icon.svg';
import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

interface Props {
  currentTest: MockTest;
  isFetching: boolean;
  onSectionOptionChange: (sectionOption: SectionOption) => void;
  recommendedTemplate: MockTestTemplate;
  sectionOptions: SectionOption[];
  selectedSectionOption: SectionOption;
  templates: MockTestTemplate[];
}

export default (props: Props) => {
  const { branding } = useAppContext().data;
  const authenticityToken = useAuthenticityToken();
  const practiceTestFormRef = useRef(null);
  const [isStarting, setIsStarting] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    if (props.recommendedTemplate) {
      setSelectedTemplate(props.recommendedTemplate);
      return;
    }

    if (branding.examName === 'LSAT' && props.selectedSectionOption.sectionTest) {
      // We don't handle recommendations for LSAT section tests, so force the user make
      // a selection.
      setSelectedTemplate(null);
    } else {
      setSelectedTemplate(props.templates.find((template) => template.available));
    }    
  }, [props.recommendedTemplate, props.selectedSectionOption.sectionTest]);

  const handleStart = async () => {
    setIsStarting(true);

    if (props.currentTest) {
      await api.finishMockTest(props.currentTest.id);
    }

    practiceTestFormRef.current.submit();
  };

  const handleTemplateChange = (e) => {
    const template = props.templates.find((template) => template.id === parseInt(e.target.value));
    setSelectedTemplate(template);
  };

  return (
    <div className="d-flex flex-column gap-m">
      <div className="d-flex flex-wrap gap-s">
        {
          props.sectionOptions.map((sectionOption) => {
            const active = sectionOption === props.selectedSectionOption;

            return (
              <div
                key={sectionOption.name}
                className={cx(
                  style.sectionOptionPill,
                  'align-items-end d-flex gap-s',
                  { [style.active]: active}
                )}
                onClick={active ? () => {} : () => props.onSectionOptionChange(sectionOption)}
              >
                <div className={`small ${active ? 'text-white' : 'text-body'}`}>
                  {sectionOption.name}
                </div>

                <div className={`tiny ${active ? 'text-white' : 'text-gray-light'}`}>
                  {minutesFormattedasHoursAndMinutesString(sectionOption.estimatedLengthInSeconds / 60, 5)}
                </div>
              </div>
            )
          })
        }
      </div>

      {
        props.selectedSectionOption.information && (
          <div className="d-flex flex-column gap-s">
            {
              props.selectedSectionOption.information.map((info, idx) => (
                <div key={idx} className="d-flex flex-column gap-xxs">
                  <h4>{info.title}</h4>
                  <div dangerouslySetInnerHTML={{ __html: info.content }} />
                </div>
              ))
            }
          </div>
        )
      }

      {
        props.templates.length > 1 && (
          <div className="d-flex flex-column gap-s">
            <h3 className="u-margin-A-n">
              <strong>
                {props.selectedSectionOption.sectionTest ? 'Section Test' : 'Full Practice Test'}
              </strong>
            </h3>

            {(() => {
              if (props.templates.some((template) => !template.explanations)) {
                return (
                  <div className="small">
                    Note: Tests marked with an asterisk do not yet have complete explanations.
                  </div>
                );
              } else if (props.templates.some((template) => template.sharedPool)) {
                return (
                  <div className="small">
                    <img alt="" src={LayersIcon} /> Shared pool tests include questions that you might have seen during practice.
                  </div>
                );
              }
            })()}

            <CustomSelect
              disabled={props.isFetching}
              isOptionDisabled={(template) => !template.available}
              maxVisibleRows={10}
              onChange={setSelectedTemplate}
              options={props.templates}
              placeholder={
                props.isFetching ? (
                  <div className="flex-1" style={{ padding: '8px 16px' }}>
                    Loading Options
                  </div>
                ) : (
                  <div className="flex-1" style={{ padding: '8px 16px' }}>
                    Choose a {props.selectedSectionOption.sectionTest ? 'section test' : 'practice test'}
                  </div>
                )
              }
              renderOption={(template) => (
                <TemplateOption
                  isRecommended={isEqual(template, props.recommendedTemplate)}
                  template={template}
                />
              )}
              renderSelected={(template) => (
                <TemplateOption
                  isRecommended={isEqual(template, props.recommendedTemplate)}
                  isSelected
                  template={template}
                />
              )}
              style={{
                width: '100%'
              }}
              triggerStyle={{
                border: `1px solid ${colors.grayLighter}`,
                borderRadius: 4,
                cursor: 'default',
                paddingRight: 16
              }}
              value={props.isFetching ? null : selectedTemplate}
            />
          </div>
        )
      }

      <form
        action={paths.practiceTests()}
        className="align-items-sm-center d-flex flex-column flex-sm-row gap-s"
        method="post"
        ref={practiceTestFormRef}
      >
        <input type="hidden" name="authenticity_token" value={authenticityToken} />
        <input type="hidden" name="mock_test[template_id]" value={selectedTemplate?.id} />

        {
          (selectedTemplate?.sectionIds || []).map((sectionId) => (
            <input
              key={sectionId}
              type="hidden"
              name="mock_test[section_ids][]"
              value={sectionId}
            />
          ))
        }

        <StartButton
          Button={SecondaryButton}
          activityName="Practice Test"
          className="flex-1 flex-sm-none"
          disabled={isStarting || props.isFetching || !selectedTemplate}
          includesSpeaking={selectedTemplate?.subjects?.indexOf('speaking') !== -1}
          onStart={handleStart}
        >
          {props.selectedSectionOption.sectionTest ? 'Start Section Test' : 'Start Practice Test'}
        </StartButton>

        {
          props.currentTest && (
            <div className="d-flex justify-content-center justify-content-sm-start small text-gray-light">
              Your in-progress test will be ended
            </div>
          )
        }
      </form>
    </div>
  );
};
